import { EyeOutlined, EditOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { getFollowUpTableData } from '@app/api/follow-up.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { Pagination } from '@app/models/Pagination';
import { FollowUpTableRow } from '@app/models/follow_up/FollowUpResponse';
import { TablePaginationConfig, Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

const FollowUpsTable = () => {
  const [tableData, setTableData] = useState<{
    data: FollowUpTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [form] = BaseButtonsForm.useForm();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);
  const lastFetchedRef = useRef(false);
  const [params, setParams] = useState<{
    current: number;
    pageSize: number;
    import_id: number | null;
  }>({
    current: 1,
    pageSize: 5,
    import_id: null,
  });

  const fetchData = async () => {
    setTableData((prev) => ({ ...prev, loading: true }));

    try {
      const res = await getFollowUpTableData(params);

      if (isMounted.current) {
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      }
    } catch (error: any) {
      setTableData({
        data: [],
        pagination: initialPagination,
        loading: false,
      });

      notificationController.error({
        message: error.message,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  const handleFilter = (values: { [key: string]: any }) => {
    setParams((prev) => ({
      ...prev,
      current: 1, // reset pagination
      import_id: values.import_id ? Number(values.import_id) : null,
    }));
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setParams((prev) => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    }));
  };

  const handleViewRow = (record: FollowUpTableRow) => {
    navigate(`/view-follow-up`, { state: { id: record.id, import_id: record.import_id, code: record.code } });
  };

  const handleEditRow = (record: FollowUpTableRow) => {
    navigate(`/edit-follow-up`, { state: { id: record.id, import_id: record.import_id, code: record.code } });
  };

  const handleDeleteRow = (record: FollowUpTableRow) => {
    console.log(`Deleting Follow Up with id ${record.id} and import ID ${record.import_id}`);
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
        {`Showing ${range[0]} - ${range[1]} of ${total} Follow Ups`}
      </span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [{ name: 'import_id', placeholder: 'Search by Import ID', type: 'text' }];

  const columns: ColumnsType<FollowUpTableRow> = [
    {
      title: 'Task Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Import ID',
      dataIndex: 'import_id',
      key: 'import_id',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
    },
    {
      title: 'Expiration Countdown',
      dataIndex: 'expire_date',
      key: 'expire_date',
      render: (text: string) => {
        if (!text) {
          return <span>N/A</span>;
        }

        const expirationDate = new Date(text);
        const now = new Date();
        const diffTime = expirationDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(diffTime / (1000 * 3600 * 24));

        const isExpiringSoon = daysLeft <= 7;
        return <span style={{ color: isExpiringSoon ? 'red' : 'inherit' }}>{daysLeft} Days Remaining</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '25%',
      render: (_, record) => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const isRestricted = user?.id === 4;

        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            {!isRestricted && (
              <>
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    onClick={() => handleEditRow(record)}
                    icon={<EditOutlined />}
                    style={{ color: '#52c41a' }}
                  />
                </Tooltip>

                <Tooltip title="Delete">
                  <Popconfirm
                    title="Are you sure to delete this follow-up?"
                    onConfirm={() => handleDeleteRow(record)}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} style={{ color: 'red' }} />
                  </Popconfirm>
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <BaseCard id="follow-ups-table" title="Follow Ups" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={handleFilter} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};

export default FollowUpsTable;
